import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { setAdminApiTokenProvider } from "../modules/admin/api/mapping-client.js";
import {
  setPlanningApiTokenProvider,
  verifyCurrentVaultUser,
} from "../modules/planning/api/client.js";
import { useAuthContext } from "./use-auth-context.js";
import { setApiSession, sessionNeedsSignIn, subscribeSessionRejection } from "./session-request.js";

/**
 * Platform route guard. While Okta is resolving auth state we render a lightweight loading state.
 * Once resolved:
 *  - authenticated → render the protected children (the module sections).
 *  - signed out → kick off the Okta PKCE sign-in redirect (auth.md §5) and, in the meantime,
 *    redirect to the in-app `/signin` route so an unauthenticated render never exposes protected
 *    content (and tests can assert the redirect without a live Okta round-trip).
 *
 * `originalUri` is captured so Okta's `restoreOriginalUri` returns the user to where they were —
 * including deep admin paths like `/admin/mapping/commission`.
 *
 * Single-document simplification: the former admin SPA loaded as a SEPARATE document with an empty
 * in-memory token store, so it ran a silent `getWithoutPrompt` dance before falling back to the
 * redirect. In the unified shell there is one document and one token manager shared by every module,
 * so that dance is gone — close and admin read the same in-memory tokens. We register the raw
 * ACCESS token with the admin AND planning API clients here, because both sit behind the shared
 * Lambda authorizer, which validates `aud` against the API resource audience `api://lio-aire`. That
 * audience only ever appears on an access token; presenting the ID token instead makes the
 * authorizer deny every call and API Gateway answer a bare 403 `Forbidden` (#120).
 */
interface RequireAuthProps {
  readonly children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { oktaAuth, authState } = useOktaAuth();
  const { isResolved, isAuthenticated, accessToken } = useAuthContext();
  const location = useLocation();
  const [identitySyncError, setIdentitySyncError] = useState<string | null>(null);
  setApiSession(accessToken, authState?.accessToken?.expiresAt);
  const sessionRejected = useSyncExternalStore(subscribeSessionRejection, sessionNeedsSignIn);
  // Latch through token removal so SDK state changes cannot restart protected requests.
  const [sessionEnded, setSessionEnded] = useState(false);
  const needsSignIn = sessionRejected || sessionEnded;
  useEffect(() => {
    if (sessionRejected) {
      setSessionEnded(true);
      oktaAuth.tokenManager.clear();
    }
  }, [sessionRejected, oktaAuth]);

  // React Query observers in module children may start their first request before this component's
  // passive effect runs. Register the in-memory access token during render as well so the very first
  // protected Admin/Planning request cannot be sent without Authorization and be cached as a 401.
  setAdminApiTokenProvider(() => accessToken);
  setPlanningApiTokenProvider(() => accessToken);

  // Keep the admin + planning API clients' Bearer token in sync with the in-memory access token.
  // Each client reads it per request, so refreshing this on every token change is enough.
  useEffect(() => {
    setAdminApiTokenProvider(() => accessToken);
    setPlanningApiTokenProvider(() => accessToken);
    return () => {
      setAdminApiTokenProvider(() => undefined);
      setPlanningApiTokenProvider(() => undefined);
    };
  }, [accessToken]);

  // Match an administrator-created email enrollment as soon as the authenticated shell opens,
  // regardless of which module was the user's landing page. The API derives both email and
  // immutable subject from the verified token; the browser sends no identity-shaped payload.
  useEffect(() => {
    if (!isResolved || !isAuthenticated || accessToken === undefined || accessToken === "") {
      setIdentitySyncError(null);
      return;
    }
    let isCurrent = true;
    void verifyCurrentVaultUser()
      .then(() => {
        if (isCurrent) setIdentitySyncError(null);
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          setIdentitySyncError(
            error instanceof Error ? error.message : "User identity verification failed",
          );
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [accessToken, isAuthenticated, isResolved]);

  useEffect(() => {
    if (isResolved && !isAuthenticated && !needsSignIn) {
      const originalUri = `${location.pathname}${location.search}${location.hash}`;
      void oktaAuth.signInWithRedirect({ originalUri }).catch(() => {
        // The sign-in page provides a retry if the redirect cannot start.
      });
    }
  }, [
    isResolved,
    isAuthenticated,
    needsSignIn,
    oktaAuth,
    location.pathname,
    location.search,
    location.hash,
  ]);

  if (needsSignIn) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
          reason: "session-ended",
        }}
      />
    );
  }

  if (!isResolved) {
    return (
      <div role="status" aria-live="polite" className="p-8 text-aire-muted">
        Checking your session…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return (
    <>
      {identitySyncError === null ? null : (
        <div
          role="alert"
          className="m-4 rounded-control border border-aire-danger/30 bg-aire-danger/10 px-3 py-2 text-sm text-aire-danger"
        >
          AIRE could not match this login to its Planning user account: {identitySyncError}
        </div>
      )}
      {children}
    </>
  );
}
